import { useEffect, useId, useRef, useState } from 'react';
import { chatbotQuickReplies } from '../data/chatbotKnowledge';
import { askChatbot, resetChatbotSession } from '../services/chatbotService';
import './ChatbotWidget.css';

const WELCOME =
  '안녕하세요! 아이로그 상담 챗봇입니다.\n학원 입점 포토부스, 도입 방법, 효과 등을 물어보세요.';

function ChatbotWidget({ onInquiry }) {
  const titleId = useId();
  const listRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 'welcome',
      role: 'bot',
      text: WELCOME,
    },
  ]);

  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open, sending]);

  const appendMessage = (role, text) => {
    setMessages((prev) => [
      ...prev,
      {
        id: `${Date.now()}-${Math.random()}`,
        role,
        text,
      },
    ]);
  };

  const sendQuestion = async (question) => {
    const trimmed = question.trim();
    if (!trimmed || sending) return;

    appendMessage('user', trimmed);
    setInput('');
    setSending(true);

    try {
      const result = await askChatbot(trimmed);
      appendMessage('bot', result.text);
    } catch (error) {
      appendMessage(
        'bot',
        error.message || '일시적인 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.'
      );
    } finally {
      setTimeout(() => setSending(false), 400);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendQuestion(input);
  };

  const handleReset = () => {
    resetChatbotSession();
    setMessages([
      {
        id: 'welcome',
        role: 'bot',
        text: WELCOME,
      },
    ]);
    setInput('');
  };

  return (
    <div className={`chatbot${open ? ' chatbot--open' : ''}`}>
      {open ? (
        <section className="chatbot__panel" aria-labelledby={titleId}>
          <header className="chatbot__header">
            <div>
              <p className="chatbot__eyebrow">i-LOG Assistant</p>
              <h2 id={titleId}>아이로그 챗봇</h2>
            </div>
            <div className="chatbot__header-actions">
              <button type="button" onClick={handleReset} aria-label="대화 초기화">
                초기화
              </button>
              <button type="button" onClick={() => setOpen(false)} aria-label="챗봇 닫기">
                ×
              </button>
            </div>
          </header>

          <div className="chatbot__messages" ref={listRef}>
            {messages.map((message) => (
              <div
                key={message.id}
                className={`chatbot__bubble chatbot__bubble--${message.role}`}
              >
                {message.text.split('\n').map((line) => (
                  <p key={`${message.id}-${line}`}>{line}</p>
                ))}
              </div>
            ))}
            {sending ? (
              <div className="chatbot__bubble chatbot__bubble--bot chatbot__typing">
                <span />
                <span />
                <span />
              </div>
            ) : null}
          </div>

          <div className="chatbot__quick">
            {chatbotQuickReplies.map((item) => (
              <button key={item} type="button" onClick={() => sendQuestion(item)} disabled={sending}>
                {item}
              </button>
            ))}
            {onInquiry ? (
              <button
                type="button"
                className="chatbot__inquiry"
                onClick={() => {
                  setOpen(false);
                  onInquiry();
                }}
              >
                도입 문의하기
              </button>
            ) : null}
          </div>

          <form className="chatbot__form" onSubmit={handleSubmit}>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="궁금한 점을 입력해 주세요"
              disabled={sending}
              aria-label="챗봇 메시지 입력"
            />
            <button type="submit" disabled={sending || !input.trim()}>
              전송
            </button>
          </form>
        </section>
      ) : null}

      <button
        type="button"
        className="chatbot__fab"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        aria-label={open ? '챗봇 닫기' : '아이로그 챗봇 열기'}
      >
        {open ? (
          <svg className="chatbot__fab-icon" viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M6.4 6.4a1 1 0 0 1 1.4 0L12 10.6l4.2-4.2a1 1 0 1 1 1.4 1.4L13.4 12l4.2 4.2a1 1 0 0 1-1.4 1.4L12 13.4l-4.2 4.2a1 1 0 0 1-1.4-1.4L10.6 12 6.4 7.8a1 1 0 0 1 0-1.4Z"
              fill="currentColor"
            />
          </svg>
        ) : (
          <svg className="chatbot__fab-icon" viewBox="0 0 48 48" aria-hidden="true">
            <path
              d="M24 6c-9.4 0-17 6.9-17 15.4 0 4.8 2.4 9.1 6.2 12l-1.5 6.3a1.4 1.4 0 0 0 2.1 1.5l7.2-3.6c1 0.2 2 0.2 3 0.2 9.4 0 17-6.9 17-15.4S33.4 6 24 6Z"
              fill="currentColor"
              opacity="0.95"
            />
            <circle cx="17.5" cy="21" r="2.2" fill="#2f6bff" />
            <circle cx="24" cy="21" r="2.2" fill="#2f6bff" />
            <circle cx="30.5" cy="21" r="2.2" fill="#2f6bff" />
          </svg>
        )}
      </button>
    </div>
  );
}

export default ChatbotWidget;
